/**
 * A link that opens the sender's chat with a reply already typed (Alex,
 * 2026-10-21), skipping WhatsApp's "Open app / Continue to WhatsApp Web"
 * page that the server's `https://wa.me/<number>` goes through.
 *
 *   - Computer: the person's own choice (whatsappTarget.js). WhatsApp Web's
 *     chat link by default, in a new tab: Alex has no desktop app, and an app
 *     link (`whatsapp://`) does nothing at all without one. With "WhatsApp
 *     app" switched on, the app link, which hands to the desktop app.
 *   - Phone: the app link, which hands straight to the installed app.
 *     WhatsApp Web does not run on a phone.
 *
 * The quoted message is capped: a forty-line price list makes a URL long
 * enough for some handlers to refuse, and the reply only needs to say which
 * message it is about.
 */
const MAX_QUOTE = 1500;

/**
 * The string with any unpaired UTF-16 surrogate replaced by U+FFFD.
 *
 * encodeURIComponent THROWS on one ("URI malformed"), and a single card doing
 * that took the whole Trade Feed down (prod, 2026-10-21 17:14). Two sources:
 * clipping a quote mid-emoji, which the code-point clip below no longer does,
 * and text that arrives with a broken emoji from the intake, which only this
 * repairs.
 */
const wellFormed = (value) =>
  typeof value.toWellFormed === "function"
    ? value.toWellFormed()
    : value.replace(/[\uD800-\uDBFF](?![\uDC00-\uDFFF])|(?<![\uD800-\uDBFF])[\uDC00-\uDFFF]/g, "\uFFFD");

export const replyText = (item) => {
  const where = wellFormed(String(item?.group_name || "WhatsApp"));
  const quote = wellFormed(String(item?.message_text || "").trim());
  // By code point, not UTF-16 unit: slice() at 1500 could cut an emoji in
  // half and leave the unpaired surrogate that crashed the page.
  const chars = Array.from(quote);
  const clipped = chars.length > MAX_QUOTE ? `${chars.slice(0, MAX_QUOTE).join("")}…` : quote;

  return `Hi, I came across your message on ${where}, is this still available?${clipped ? `\n${clipped}` : ""}`;
};

/** A phone or tablet, where the WhatsApp app (not WhatsApp Web) is the way in. */
export const isMobileDevice = () =>
  typeof navigator !== "undefined" && /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent || "");

/**
 * @returns {{ href: string, newTab: boolean } | null}  null without a number.
 */
const whatsappChatLink = (item, target = "web", mobile = isMobileDevice()) => {
  const digits = String(item?.sender_phone || "").replace(/\D+/g, "");
  if (!digits) return null;

  // Never let one card's text break the page: if the reply still cannot be
  // encoded, open the chat without it.
  let text = "";
  try {
    text = `&text=${encodeURIComponent(replyText(item))}`;
  } catch {
    text = "";
  }
  const query = `phone=${digits}${text}`;

  return mobile || target === "app"
    ? { href: `whatsapp://send?${query}`, newTab: false }
    : { href: `https://web.whatsapp.com/send?${query}`, newTab: true };
};

export default whatsappChatLink;
